import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, fmtErr } from "@/lib/api";
import { useGuest } from "@/context/GuestContext";
import BrandLogo from "@/components/BrandLogo";
import { BookingSummaryCard, LookupStep, GuestInfoStep, StayDetailsStep, PreferencesStep, IdentityStep, ConsentStep } from "@/pages/guest/checkin/CheckInSteps";

export default function GuestCheckInPage() {
  const nav = useNavigate();
  const { refresh } = useGuest();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lookupMobile, setLookupMobile] = useState("");
  const [bookingRefInput, setBookingRefInput] = useState("");
  const [showRefInput, setShowRefInput] = useState(false);
  const [lookupErr, setLookupErr] = useState("");
  const [foundBooking, setFoundBooking] = useState(null);
  const [f, setF] = useState({
    full_name: "", mobile: "", email: "", date_of_birth: "",
    adults: 2, children: 0, travel_from: "", travel_to: "",
    check_in_date: new Date().toISOString().slice(0, 10),
    check_out_date: new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10),
    food_preferences: "Vegetarian", allergies: "", special_occasion: "",
    special_requests: "", marketing_consent: false, privacy_consent: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [emailErr, setEmailErr] = useState("");
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email || "");
  const [idCfg, setIdCfg] = useState({ id_requirement: "PRIMARY_ONLY", id_types: [], max_file_mb: 5 });
  const [idDocs, setIdDocs] = useState([]);
  const [idErr, setIdErr] = useState("");
  const [idBusy, setIdBusy] = useState(false);
  const requiredIds = idCfg.id_requirement === "EVERY_ADULT" ? Math.max(1, parseInt(f.adults) || 1) : 1;

  useEffect(() => {
    http.get("/public/checkin-config").then((r) => setIdCfg(r.data)).catch(() => {});
  }, []);

  const loadIds = async (mobile) => {
    try {
      const r = await http.get("/guest/checkin/id-uploads", { params: { mobile } });
      setIdDocs(r.data.ids || []);
    } catch { /* list refresh is best-effort */ }
  };

  const onIdFile = async (file, idType, label, clientErr) => {
    if (clientErr) { setIdErr(clientErr); return; }
    setIdBusy(true); setIdErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("id_type", idType);
      fd.append("mobile", f.mobile);
      if (f.booking_reference) fd.append("booking_reference", f.booking_reference);
      if (label) fd.append("guest_label", label);
      await http.post("/guest/checkin/id-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadIds(f.mobile);
    } catch (e) { setIdErr(fmtErr(e)); } finally { setIdBusy(false); }
  };

  const onIdDelete = async (docId) => {
    setIdBusy(true); setIdErr("");
    try {
      await http.delete(`/guest/checkin/id-upload/${docId}`, { params: { mobile: f.mobile } });
      await loadIds(f.mobile);
    } catch (e) { setIdErr(fmtErr(e)); } finally { setIdBusy(false); }
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await http.post("/guest/checkin", f);
      await refresh();
      nav("/guest/dashboard");
    } catch (e) { setErr(fmtErr(e)); } finally { setBusy(false); }
  };

  const canNext = () => {
    if (step === 1) return f.full_name && f.mobile && f.email;
    if (step === 2) return f.check_in_date && f.check_out_date && f.adults >= 1;
    if (step === 3) return true;
    if (step === 4) return idDocs.length >= requiredIds;
    if (step === 5) return f.privacy_consent;
    return false;
  };

  const onContinue = () => {
    if (!canNext()) return;
    if (step === 1 && !emailOk) {
      setEmailErr("Please enter a valid email address (it must contain an @ sign).");
      return;
    }
    setEmailErr("");
    if (step + 1 === 4) loadIds(f.mobile);
    setStep(step + 1);
  };

  const doLookup = async () => {
    setBusy(true); setLookupErr("");
    try {
      const r = await http.post("/guest/lookup-booking", {
        mobile: lookupMobile,
        booking_reference: bookingRefInput || undefined,
      });
      if (r.data.found) {
        setF((p) => ({ ...p, ...r.data.prefill }));
        setFoundBooking(r.data.booking);
        setStep(1);
      } else if (r.data.multiple) {
        setShowRefInput(true);
        setLookupErr(r.data.message);
      }
    } catch (e) {
      setShowRefInput(true);
      setLookupErr(fmtErr(e));
    } finally { setBusy(false); }
  };

  const onEmailChange = (e) => { set("email", e.target.value); setEmailErr(""); };

  return (
    <div className="min-h-screen" style={{ background: "var(--surface)" }}>
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="mb-6"><BrandLogo className="h-12" /></div>
        <h1 className="font-serif text-4xl mb-1">Welcome</h1>
        <p className="mb-6" style={{ color: "var(--muted)" }}>Complete a quick check-in to unlock your stay.</p>

        {foundBooking && <BookingSummaryCard booking={foundBooking} />}

        {step >= 1 && (
        <div className="flex gap-2 mb-6">
          {[1, 2, 3, 4, 5].map((s) => (
            <div key={s} className="flex-1 h-1 rounded" style={{ background: step >= s ? "var(--terracotta)" : "var(--border)" }} />
          ))}
        </div>
        )}

        <div className="card fadein">
          {step === 0 && (
            <LookupStep
              lookupMobile={lookupMobile} setLookupMobile={setLookupMobile}
              bookingRefInput={bookingRefInput} setBookingRefInput={setBookingRefInput}
              showRefInput={showRefInput} lookupErr={lookupErr} busy={busy}
              onLookup={doLookup} onManual={() => setStep(1)}
            />
          )}
          {step === 1 && <GuestInfoStep f={f} setField={set} onEmailChange={onEmailChange} emailErr={emailErr} />}
          {step === 2 && <StayDetailsStep f={f} setField={set} />}
          {step === 3 && <PreferencesStep f={f} setField={set} />}
          {step === 4 && (
            <IdentityStep idCfg={idCfg} idDocs={idDocs} requiredCount={requiredIds} busy={idBusy}
              onFile={onIdFile} onDelete={onIdDelete} err={idErr} />
          )}
          {step === 5 && <ConsentStep f={f} setField={set} err={err} />}
        </div>

        <div className="flex gap-3 mt-6">
          {step > 1 && <button data-testid="checkin-back" onClick={() => setStep(step - 1)} className="btn-outline flex-1">Back</button>}
          {step >= 1 && step < 5 && <button data-testid="checkin-next" onClick={onContinue} disabled={!canNext()} className="btn-primary flex-1">Continue</button>}
          {step === 5 && <button data-testid="checkin-submit" onClick={submit} disabled={!canNext() || busy} className="btn-primary flex-1">{busy ? "Checking you in…" : "Complete Check-In"}</button>}
        </div>
      </div>
    </div>
  );
}
